import { consoleLogger } from '../ports/logger.js';
/** Signed media URLs live 60 min (R2 default); refresh well before expiry. */
export const NETWORK_URL_MAX_AGE_MS = 45 * 60 * 1000;
/**
 * Fresh network URLs for videos played straight from the network (browsers that
 * can't play blob: video). Re-fetching the manifest makes the API re-sign every
 * item URL; results are reused until they near expiry. Falls back to the
 * manifest's own URL if the refresh fails.
 */
export function createNetworkVideoUrlResolver(input) {
    const logger = input.logger ?? consoleLogger;
    const maxAgeMs = input.maxAgeMs ?? NETWORK_URL_MAX_AGE_MS;
    const now = input.now ?? Date.now;
    const fresh = new Map();
    const inflight = new Map();
    async function load(ref, key) {
        const credential = await input.credentials.get();
        if (!credential)
            return null;
        const manifest = await input.api.getManifest(credential.token, {
            playlistId: ref.playlistId,
            version: ref.version,
        });
        const urls = new Map(manifest.items.map((entry) => [entry.assetId, entry.url]));
        fresh.set(key, { fetchedAt: now(), urls });
        return urls;
    }
    return async (ref, item) => {
        const key = `${ref.playlistId}:${ref.version}`;
        const cached = fresh.get(key);
        if (cached && now() - cached.fetchedAt < maxAgeMs) {
            return cached.urls.get(item.assetId) ?? item.url;
        }
        let pending = inflight.get(key);
        if (!pending) {
            pending = load(ref, key).finally(() => inflight.delete(key));
            inflight.set(key, pending);
        }
        try {
            const urls = await pending;
            return urls?.get(item.assetId) ?? item.url;
        }
        catch (error) {
            logger.warn('Network URL refresh failed — using manifest URL', {
                assetId: item.assetId,
                error: error instanceof Error ? error.message : String(error),
            });
            return item.url;
        }
    };
}
//# sourceMappingURL=network-video-url.js.map